import React, { Fragment, useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid2';
import { IconButton } from '@mui/material';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';

import localData from 'utils/localData';
import api from 'utils/api';

import Editor from './Editor';
import Preview from './Preview';
import { PageLoader, PageError } from 'components';
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';
import { EditorGrid, InvoiceEditorGrid, PreviewGrid } from './styles';
import { LANGUAGE } from 'shared/constants/users';
import {
  formatDateForLanguage,
  parseStringToNumber,
} from 'views/Billing/shared/utils';
import { getTagContent, getTagDisplayName } from './utils';

// Create a logger instance for this component
const log = logger.createLogger('InvoiceEditor');

const propTypes = {
  invoiceToBillDate: PropTypes.string.isRequired,
  billData: PropTypes.any.isRequired,
  billDataKenshin: PropTypes.any.isRequired,
  invoiceToOrgId: PropTypes.number.isRequired,
  invoiceToOrgName: PropTypes.string.isRequired,
  invoiceTotalAmount: PropTypes.number,
  modalClose: PropTypes.func.isRequired,
};

const PreviewMemo = React.memo(Preview);

const getInitInvoiceContent = (
  invoiceToBillDate,
  billData,
  billDataKenshin,
  invoiceToOrgId,
  invoiceToOrgName,
  invoiceFromOrgInfo
  //invoiceTotalAmount // Currently not used as we are not allowing edition on the price
) => {
  const language = localData.get(USER_DATA.PREFFERED_LANGUAGE);
  let initInvoiceContent = {
    invoiceDate: '',
    invoiceTo: '',
    invoiceTo2: '',
    invoiceFrom: '',
    invoiceGreetings: '',
    invoiceDetailedTitle: '',
    invoiceTotalAmount: 0,
    invoiceTaxRate: 10,
    invoiceTotalAmountIncludingTax: 0,
    invoiceDetailedAmount: '',
    invoicePaymentMethod: '',
    invoiceRemarks: '',
    userAddedBills: [],
  };

  // 1. invoiceData: today
  const today = new Date();
  initInvoiceContent.invoiceDate =
    today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate();

  // 2. invoiceTo
  if (localData.get(USER_DATA.PREFFERED_LANGUAGE) === LANGUAGE.JAPANSESE) {
    initInvoiceContent.invoiceTo =
      invoiceToOrgName + intl.get('billing_invoice_text_dear');
  } else {
    initInvoiceContent.invoiceTo =
      intl.get('billing_invoice_text_dear') + invoiceToOrgName;
  }

  // 3. invoiceFrom
  initInvoiceContent.invoiceFrom =
    invoiceFromOrgInfo.organizationName +
    '\n' +
    invoiceFromOrgInfo.contact.name +
    '\n' +
    '〒' +
    invoiceFromOrgInfo.contact.zipCode +
    '\n' +
    invoiceFromOrgInfo.contact.addressLine1 +
    invoiceFromOrgInfo.contact.addressLine2 +
    '\n' +
    'TEL: ' +
    invoiceFromOrgInfo.contact.tel +
    '\n' +
    'FAX: ' +
    invoiceFromOrgInfo.contact.fax;
  // 4. invoiceGreetings
  initInvoiceContent.invoiceGreetings = intl.get(
    'billing_invoice_text_greetings'
  );

  // 5. invoiceDetailedTitle
  const selectedBillRawData = billData.find(
    (bill) =>
      bill.orgId === invoiceToOrgId &&
      bill.billedMonthYear === invoiceToBillDate
  );
  const billingMonthString =
    formatDateForLanguage(selectedBillRawData.startDate) +
    ' - ' +
    formatDateForLanguage(selectedBillRawData.endDate);
  initInvoiceContent.invoiceDetailedTitle =
    intl.get('billing_invoice_text_detailed_title_default') +
    ' (' +
    billingMonthString +
    ')';

  let totalAmountToPay = 0;

  // 7. invoiceDetailedAmount
  // 7.1 Get bill data detailed amount
  let selectedBillData = [];
  if (!selectedBillRawData) {
    selectedBillData = [];
  } else {
    selectedBillData = selectedBillRawData.bills;
  }

  const mapForBillData = new Map();
  let adjustmentAmount = {
    count: 0,
    totalAmount: 0,
  };

  selectedBillData.forEach((bill, _) => {
    const tagIds = bill.tagIds;
    const tags = JSON.parse(bill.tags);
    const changeHistory = JSON.parse(bill.changeHistory);

    // for special rules
    tagIds.forEach((tagId) => {
      //! Check if the tagId is not in tags
      if (!tags.some((tag) => tag.id === tagId)) {
        log.warn('warning the tagID is not in tags', tagId);
        return;
      }

      const tag = tags.filter((tag) => tag.id === tagId)[0];
      const tagContent = getTagContent(tag);
      const tagUpdatedAt = new Date(tag.updatedAt).getTime();
      const mapTagId = tagId + '-' + tagUpdatedAt;

      const changeForThisTag = changeHistory.filter(
        (history) => history.tagId === tagId && history.type === 'rule'
      );

      let changeFromRule = {};
      changeForThisTag.forEach((change) => {
        totalAmountToPay += parseStringToNumber(change.feeChange);
        changeFromRule[change.ruleIndex] = {
          count: 1,
          feeChange: change.feeChange,
          remark: change.remark,
        };
      });

      //! Tag ID is the tagId and the updatedAt timestamp
      // This is due to the fact that the tag can be edited during the billing period
      const statics = mapForBillData.get(mapTagId);

      let newStatics = {};

      // Adding extra fees
      // 至急 pricing
      const oldChangeFromRule = statics ? statics.changeFromRule : {};
      let newChangeFromRule = {
        ...oldChangeFromRule,
      };
      // Calculating new fees
      Object.keys(changeFromRule).forEach((key) => {
        if (newChangeFromRule[key]) {
          newChangeFromRule[key] = {
            count: oldChangeFromRule[key].count + 1,
            feeChange: oldChangeFromRule[key].feeChange,
            remark: oldChangeFromRule[key].remark,
          };
        } else {
          newChangeFromRule[key] = {
            ...changeFromRule[key],
          };
        }
      });

      totalAmountToPay += statics
        ? parseStringToNumber(statics.fees.defaultFee)
        : parseStringToNumber(tagContent.defaultFee);

      newStatics = {
        labelText: statics
          ? statics.labelText
          : getTagDisplayName(tagContent, language),
        fees: statics
          ? {
              count: statics.fees.count + 1,
              defaultFee: parseStringToNumber(statics.fees.defaultFee),
            }
          : {
              count: 1,
              defaultFee: parseStringToNumber(tagContent.defaultFee),
            },
        changeFromRule: newChangeFromRule,
      };

      // Create a new static for each price tag
      mapForBillData.set(mapTagId, newStatics);
    });
  });

  //! 7.2 Get kenshin bill data detailed amount
  const selectedKenshinRawData = billDataKenshin.find(
    (bill) =>
      bill.orgId === invoiceToOrgId &&
      bill.billedMonthYear === invoiceToBillDate
  );

  let selectedKenshinBillData = [];
  if (!selectedKenshinRawData) {
    selectedKenshinBillData = [];
  } else {
    selectedKenshinBillData = selectedKenshinRawData.bills;
  }
  const mapForBillDataKenshin = new Map();
  let adjustmentAmountKenshin = {
    count: 0,
    totalAmount: 0,
  };

  selectedKenshinBillData.forEach((bill) => {
    if (bill.tagCount === null) return;
    const tagCount = JSON.parse(bill.tagCount);

    for (let key in tagCount) {
      const tagId = parseInt(key);
      const tag = tagCount[key];
      const statics = mapForBillDataKenshin.get(tagId);

      if (!statics) {
        mapForBillDataKenshin.set(tagId, tag);
      } else {
        totalAmountToPay += statics.fee * statics.count;
        const newStatics = {
          count: statics.count + tag.count,
          fee: statics.fee,
          labelText: statics.labelText,
        };
        mapForBillDataKenshin.set(tagId, newStatics);
      }
    }
  });

  // 6. invoiceTotalAmount
  // 6.1 total amount
  initInvoiceContent.invoiceTotalAmount = totalAmountToPay;

  // 6.2 total amount including tax
  initInvoiceContent.invoiceTotalAmountIncludingTax =
    (totalAmountToPay * (100 + initInvoiceContent.invoiceTaxRate)) / 100;

  // Final invoice content
  initInvoiceContent.invoiceDetailedAmount = {
    // Seimitsu
    mapForBillData: mapForBillData,
    // Adjustment
    adjustmentAmount: adjustmentAmount,
    // Kenshin
    mapForBillDataKenshin: mapForBillDataKenshin,
    // Adjustment Kenshin
    adjustmentAmountKenshin: adjustmentAmountKenshin,
  };

  // 8. invoicePaymentMethod
  initInvoiceContent.invoicePaymentMethod =
    invoiceFromOrgInfo.paymentMethod.bank +
    ' ' +
    invoiceFromOrgInfo.paymentMethod.branch +
    '\n' +
    invoiceFromOrgInfo.paymentMethod.accountType +
    ' ' +
    invoiceFromOrgInfo.paymentMethod.accountNo +
    '\n' +
    invoiceFromOrgInfo.paymentMethod.accountName;

  // 9. invoiceRemarks
  initInvoiceContent.invoiceRemarks = '';

  return initInvoiceContent;
};

const InvoiceEditor = ({
  invoiceToBillDate,
  billData,
  billDataKenshin,
  invoiceToOrgId,
  invoiceToOrgName,
  invoiceTotalAmount,
  modalClose,
}) => {
  //TODO: Generate the init invoice content
  const { enqueueSnackbar } = useSnackbar();
  const [isError, setIsError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [invoiceContent, setInvoiceContent] = useState({});

  //! This should be the same as the invoice content
  const [previewContent, setPreviewContent] = useState({});

  const organizationId = localData.get(USER_DATA.CURRENT_ORGANIZATION_ID);

  useEffect(() => {
    setLoaded(false);
    const apiVariables = {
      params: {
        organizationId: parseInt(organizationId),
      },
    };

    api
      .get(DOLPHIN_API.ORGANIZATION_PREFERENCE, apiVariables)
      .then(async (data) => {
        // TODO: generate init invoice content
        const invoiceFromOrgInfo = data;
        // TODO remove the console.logs and the try catch
        try {
          const initInvoiceContent = getInitInvoiceContent(
            invoiceToBillDate,
            billData,
            billDataKenshin,
            invoiceToOrgId,
            invoiceToOrgName,
            invoiceFromOrgInfo
          );
          setInvoiceContent(initInvoiceContent);
          setPreviewContent(initInvoiceContent);
          setLoaded(true);
        } catch (error) {
          log.error('error', error);
          setLoaded(false);
          // TODO A better error to show the user
          enqueueSnackbar(error.message, { variant: 'error' });
        }
      })
      .catch((_) => {
        setLoaded(false);
        setIsError(true);
      });
  }, [
    billData,
    billDataKenshin,
    invoiceToOrgId,
    invoiceToOrgName,
    invoiceTotalAmount,
    organizationId,
    invoiceToBillDate,
  ]);

  if (!loaded) {
    return <PageLoader />;
  }

  if (isError) {
    return <PageError />;
  }

  return (
    <Fragment>
      <Grid>
        <Grid
          container
          justifyContent="flex-end"
          sx={{ backgroundColor: '#525659' }}>
          <div>
            <IconButton onClick={modalClose}>
              <CloseIcon sx={{ color: '#fff' }} />
            </IconButton>
          </div>
        </Grid>
        <InvoiceEditorGrid
          container
          direction="row"
          justifyContent="center" // Centers the grid items horizontally
          alignItems="center">
          {/* // Editor Grid */}
          <EditorGrid size={6}>
            <Editor
              invoiceContent={invoiceContent}
              setInvoiceContent={setInvoiceContent}
              setPreviewContent={setPreviewContent}
            />
          </EditorGrid>
          {/* // Preview Grid */}
          <PreviewGrid size={6}>
            <PreviewMemo
              invoiceContent={previewContent}
              shouldShowPreview={true}
            />
          </PreviewGrid>
        </InvoiceEditorGrid>
      </Grid>
    </Fragment>
  );
};

InvoiceEditor.propTypes = propTypes;

export default InvoiceEditor;
